// FunnelBars.tsx
import { cn } from "@/lib/utils";

import candidateStatusToColors from "@/utils/candidateStatusToColors";
import candidateStatusToLabel from "@/utils/candidateStatusToLabels";

import {
  CandidateWaitingRoomStatus,
  candidateWaitingRoomStatusList,
} from "@/types";
import { AdminPipelineSummary } from "@/queries/admin/candidate-pipeline";

interface Step {
  label: CandidateWaitingRoomStatus;
  value: number;
}

export function FunnelBars({
  steps,
  showPercent = true,
  className,
}: {
  steps: Step[];
  showPercent?: boolean;
  className?: string;
}) {
  const max = Math.max(...steps.map(({ value }) => value));

  return (
    <div className={cn("w-full space-y-2", className)}>
      {steps.map((step, idx) => {
        const label = candidateStatusToLabel(step.label);
        const { backgroundColor } = candidateStatusToColors(step.label);
        const percentage = Math.round((step.value / max) * 100);

        return (
          <div key={`step-${idx}`}>
            <div className="flex items-center gap-3">
              <div className="w-40 shrink-0 text-sm text-gray-700">{label}</div>
              <div className="flex-1 justify-items-center">
                <div
                  className={cn("h-8 rounded-md", backgroundColor)}
                  style={{ width: `${percentage}%` }}
                  aria-valuenow={step.value}
                  aria-valuemax={max}
                  role="progressbar"
                />
              </div>
              <div className="w-28 shrink-0 text-right text-sm tabular-nums text-gray-800">
                {step.value.toLocaleString()}{" "}
                {showPercent && (
                  <span className="text-gray-500">({percentage}%)</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const CandidateFunnel = ({ summary }: { summary?: AdminPipelineSummary }) => {
  if (!summary) {
    return;
  }

  const steps: Step[] = candidateWaitingRoomStatusList
    // We'll merge "Joined", "Rejected", and "Dropped out"
    .filter((stage) => stage !== "Rejected" && stage !== "Dropped out")
    .map((stage) => ({
      label: stage,
      value: 0,
    }));

  const finalStepCount =
    // eslint-disable-next-line @typescript-eslint/dot-notation
    summary["Joined"] + summary["Rejected"] + summary["Dropped out"];

  let cumm = 0;
  for (let i = steps.length - 1; i >= 0; i--) {
    const step = steps[i]!;
    const count =
      step.label === "Joined" ? finalStepCount : summary[step.label];

    cumm += count;

    step.value = cumm;
  }

  return <FunnelBars steps={steps} showPercent />;
};

export default CandidateFunnel;
